import { useState } from 'react'
import { Bot, RefreshCw, Square, Save, TrendingUp, AlertTriangle, FlaskConical, Wand2, Play, SlidersHorizontal, Power } from 'lucide-react'
import { getAdminBots, adminStopBot, getAdminTrading, getAdminDemoTrading, getAdminRecommendMode, getAdminConfig, saveAdminConfig, getAdminBotSnapshot, adminSaveBotSnapshot, adminRestoreBots } from '../../services/api'
import {
  C, Card, Row, Btn, Chips, BigStat, Loading, ErrorLine, Empty,
  useAsync, fmtDateTime, fmtMoney, fmtInt, userLabel, Pill, divider, confirmAsync,
  Toggle, inputStyle, labelStyle,
} from './adminUI'

// Раздел «Боты и торговля»: мониторинг ботов ВСЕХ юзеров + торговая статистика.
// ВАЖНО: работающий бот имеет status = 'active' (так пишет updateBotStatus).
// alive приходит с бэкенда: есть ли живой процесс с записанным pid. Бот
// со status='active', но alive=false — «зомби» (процесс умер, БД не знает),
// такие подсвечиваем: их надо остановить, иначе юзер видит «работает».
//
// Список ботов ПО ЮЗЕРАМ (task.txt, 2026-09-30): «этот раздел получается слишком
// длинный… чтобы в этом разделе можно было просто листать этот список, так как в
// будущем этот список будет очень большим». Поэтому список живёт в рамке
// фиксированной высоты и прокручивается, а грузится порциями по LB_PAGE.
const LB_PAGE = 60
const LB_PAGE_MAX = 500        // потолок adminBotsOverview (db.js: limit 1..500)

const STATUS = {
  active:  { label: 'работает', color: C.accent },
  stopped: { label: 'остановлен', color: C.dim },
  error:   { label: 'ошибка', color: C.danger },
}

function TradeBucket({ title, b }) {
  if (!b) return null
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <BigStat label="Сделок" value={b.trades} color="#fff" />
        <BigStat label="PNL" value={fmtMoney(b.pnl)} color={b.pnl >= 0 ? C.accent : C.danger} />
        <BigStat label="Winrate" value={`${b.winRate}%`} color={C.indigo} />
      </div>
    </div>
  )
}

// PNL без округления: в дневнике юзер видит полную точность биржи, и админка
// не должна показывать другое число (иначе «в админке 5.12, у юзера 5.1187»).
const fmtPnlFull = (x) => {
  const n = Number(x || 0)
  let s = Math.abs(n).toFixed(8).replace(/0+$/, '')
  const [i, f = ''] = s.split('.')
  s = `${i}.${f.padEnd(2, '0')}`
  return `${n < 0 ? '-' : ''}${s}`
}

// Время сделки для карточки бота. Колонку trades.closed_at движок пишет уже
// готовой UTC+3-строкой («2026-10-06 11:00:00 UTC+3»), а штатный fmtDateTime
// считает вход UTC и прибавляет ADMIN_TZ_OFFSET_MIN — для этой колонки он показал бы
// время на 3 часа позже (и разошёлся бы с дневником юзера, где те же строки
// рисуются как есть: DiaryPage, closedAt.slice). Поэтому берём дату и время из
// строки напрямую — как в дневнике.
const fmtClosedAt = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/)
  return m ? `${m[3]}.${m[2]} ${m[4]}:${m[5]}` : (s ? String(s) : '—')
}

// Рекомендуемый режим для юзеров (task.txt п.4): админ выставляет ВРУЧНУЮ,
// какой режим (long/short) юзеры увидят как рекомендуемый при создании бота.
// Ниже — аналитика рынка (п.5): оценка по живым ставкам фандинга с Bybit,
// только как подсказка для решения админа (не авто-назначение).
function RecommendModeCard() {
  const cfg = useAsync(() => getAdminConfig(), [])
  const [rec, setRec] = useState(null) // локальный выбор до сохранения (null = текущий из конфига)
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState('')
  const [data, setData] = useState(null) // аналитика
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const savedVal = cfg.data?.recommendedTradeMode === 'long' ? 'long' : 'short'
  const current = rec ?? savedVal
  const changed = rec !== null && rec !== savedVal
  // Режимы торговли (task.txt п.7): кнопки выключенных направлений не
  // показываем — рекомендация не может указывать на запрещённый режим.
  // Если текущая рекомендация оказалась отключённой — показываем остальной.
  const modes = cfg.data?.tradeModes || {}
  const enabledModes = [
    modes.longEnabled !== false && 'long',
    modes.shortEnabled !== false && 'short',
  ].filter(Boolean)
  const visibleModes = enabledModes.length ? enabledModes : ['long', 'short']

  const recommend = async () => {
    setLoading(true); setError(''); setData(null)
    try {
      setData(await getAdminRecommendMode())
    } catch (e) { setError(e?.message || e) }
    finally { setLoading(false) }
  }

  const save = async () => {
    if (!rec || saving) return
    setSaving(true); setNote('')
    try {
      await saveAdminConfig({ recommendedTradeMode: rec })
      setNote('Рекомендация сохранена — юзеры увидят её при создании бота')
      cfg.reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setSaving(false) }
  }

  // Кнопки как у юзерской формы: активная long — зелёная, активная short — серая.
  const btn = (m) => {
    const active = current === m
    return {
      flex: 1, padding: '10px 0', borderRadius: 10,
      fontSize: 13, fontWeight: 700, border: 'none',
      cursor: 'pointer', transition: 'all 0.2s',
      background: active
        ? (m === 'long' ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                        : 'linear-gradient(135deg, #636366 0%, #48484A 100%)')
        : 'rgba(255,255,255,0.05)',
      color: active ? '#fff' : '#636366',
    }
  }

  const isLong = data?.recommendation === 'LONG'
  const modeColor = isLong ? C.accent : C.warn
  const modeLabel = isLong ? 'Long · сбор фандинга' : 'Short · сбор падений'

  return (
    <Card title="Рекомендуемый режим для юзеров" icon={Wand2} accent={C.indigo}>
      <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5, marginBottom: 10 }}>
        Выставляется вручную: какой режим выберете — тот юзеры увидят как
        рекомендуемый при создании бота (обычно это short — сбор падений).
        Ниже — аналитика рынка как подсказка для решения.
      </div>
      <ErrorLine onRetry={cfg.reload}>{cfg.error}</ErrorLine>
      <div style={{ display: 'flex', gap: 8 }}>
        {visibleModes.map(m => (
          <button key={m} type="button" onClick={() => setRec(m)} style={btn(m)}>
            {m}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <Btn small icon={Save} disabled={!changed || saving} onClick={save}>
          {saving ? '…' : 'Сохранить'}
        </Btn>
        <span style={{ fontSize: 11, color: C.dim, flex: 1 }}>
          Юзерам сейчас показывается: {savedVal}
        </span>
      </div>
      {!enabledModes.includes(savedVal) && (
        <div style={{
          marginTop: 8, fontSize: 11.5, lineHeight: 1.45,
          color: C.danger, background: 'rgba(248,113,113,0.08)',
          border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, padding: '8px 10px',
        }}>
          Текущая рекомендация «{savedVal}» отключена в карточке «Режимы торговли»
          ниже — юзеры при создании бота увидят рекомендацию, которая приходит из
          API с уже переключённым режимом (Long/Short автоматически). Выберите
          активное направление и сохраните, чтобы не сбивало с толку.
        </div>
      )}
      {note && (
        <div style={{
          fontSize: 11, marginTop: 6,
          color: note.startsWith('Ошибка') ? C.danger : C.accent,
        }}>
          {note}
        </div>
      )}
      <div style={divider} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: C.dim, flex: 1 }}>Аналитика рынка — подсказка для решения</div>
        <Btn small ghost icon={RefreshCw} color={C.muted} onClick={recommend} disabled={loading}>
          {loading ? 'Считаю…' : 'Проверить сейчас'}
        </Btn>
      </div>
      <ErrorLine onRetry={recommend}>{error}</ErrorLine>
      {!data ? (
        !loading && !error ? (
          <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5 }}>
            Оценит текущие ставки фандинга на Bybit и подскажет, какой режим бота
            сейчас выгоднее (Long — сбор выплат, Short — сбор падений после выплаты).
            Это только подсказка — решение принимаете вы.
          </div>
        ) : loading ? <Loading /> : null
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{
              fontSize: 14, fontWeight: 700, color: modeColor,
              background: `${modeColor}18`, border: `1px solid ${modeColor}44`,
              borderRadius: 10, padding: '8px 14px',
            }}>
              {modeLabel}
            </div>
            {data.cacheAgeSec != null && (
              <div style={{ fontSize: 10.5, color: C.dim }}>
                данные Bybit: {data.cacheAgeSec < 120 ? 'свежие' : `${data.cacheAgeSec}с назад`}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: '#fff', lineHeight: 1.5, marginBottom: 10 }}>
            {(data.reasons || []).map((r, i) => <div key={i}>{r}</div>)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <BigStat label="Глубоких ≤ порога" value={data.stats?.deep ?? 0} color={modeColor} />
            <BigStat label="Отрицательных" value={data.stats?.negative ?? 0} color="#fff" />
            <BigStat label="Всего пар" value={data.stats?.total ?? 0} color={C.indigo} />
          </div>
          {!!(data.topSymbols || []).length && (
            <>
              <div style={divider} />
              <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>
                Монеты с самой глубокой ставкой:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.topSymbols.map(s => (
                  <span key={s.symbol} style={{
                    fontSize: 10.5, fontFamily: 'monospace', color: C.indigo,
                    background: 'rgba(129,140,248,0.08)', border: `1px solid ${C.line}`,
                    borderRadius: 8, padding: '3px 8px',
                  }}>
                    {s.symbol} {s.ratePct}%
                  </span>
                ))}
              </div>
            </>
          )}
          {/* Анализ выплат (task.txt п.10-11): свечное поведение и цены в
              пределах 5 минут после каждой выплаты с порогом −0,6% — заполняет
              Ishodnk/analyzer.py. Строка-подсказка + сводка по эпизодам. */}
          {data.analysis && (
            <>
              <div style={divider} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: C.dim, flex: 1 }}>
                  Анализ выплат за {data.analysis.windowH || 72} ч —
                  свечное поведение после выплаты (порог −{(data.analysis.thresholdPct ?? 0.6)}%)
                </div>
                {data.analysis.lastUpdate && (
                  <span style={{ fontSize: 10.5, color: C.dim }}>
                    {fmtDateTime(data.analysis.lastUpdate)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <BigStat label="SHORT-favorable" value={data.analysis.shortCount ?? 0} color={C.warn} />
                <BigStat label="LONG-favorable" value={data.analysis.longCount ?? 0} color={C.accent} />
                <BigStat label="Нейтральных" value={data.analysis.neutralCount ?? 0} color={C.dim} />
              </div>
              {(data.analysis.perExchange || []).map(ex => (
                <Row
                  key={ex.exchange}
                  label={`${ex.exchange}: SHORT ${ex.shortCount ?? 0} · LONG ${ex.longCount ?? 0} · нейтр. ${ex.neutralCount ?? 0}`}
                  value={ex.episodes ? `${ex.episodes} эпизодов` : 'нет данных'}
                  valueColor={C.dim}
                  valueSub="цена после выплаты: падение → SHORT; рост без дохода к SL → LONG"
                />
              ))}
              {!(data.analysis.perExchange || []).length && (
                <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.45 }}>
                  Отдельных эпизодов по биржам в снимке пока нет — анализатор
                  накопит их после ближайших выплат по порогу.
                </div>
              )}
            </>
          )}
        </>
      )}
    </Card>
  )
}

// «Параметры ботов по умолчанию» (task.txt п.6, 2026-09-17): админ задаёт
// дефолты TP/SL/Volume/Leverage/порогов фандинга для создания ботов LONG и
// SHORT. Форма у юзера и виртуальный путь POST /api/bots читают их оттуда.
// Правки уходят в admin_config.json через saveAdminConfig({ botDefaults }).
function BotDefaultsCard() {
  const cfg = useAsync(() => getAdminConfig(), [])
  const [tab, setTab] = useState('long') // 'long' | 'short'
  // Локальная копия дефолтов до сохранения. Форма — ТА ЖЕ, что в конфиге:
  // {long:{…}, short:{…}}. До ответа сервера — пустые вкладки (поля покажут
  // подсказки-плейсхолдеры).
  const [vals, setVals] = useState(null)
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState('')

  // 2026-09-20: ЗДЕСЬ БЫЛ БАГ «в полях ничего нельзя изменить».
  // Состояние хранится по режиму ({long:…, short:…}), а значение поля искали
  // как current[f.key] — то есть у объекта с ключами long/short спрашивали
  // TakeProfit. Пока vals === null, current был правильным (savedFor(tab)), и
  // поля показывали значения из конфига; но после ПЕРВОГО же символа vals
  // становился истинным, current делался объектом режимов, current['TakeProfit']
  // не существовал → поле мгновенно переписывалось пустым (или значением с
  // другой вкладки), и набрать ничего было нельзя.
  // Там же был второй дефект: setFor раскладывал ИМЕНА ПОЛЕЙ в верхний уровень
  // ({...savedFor('long'), ...savedFor('short')}) вместо ключей long/short, а
  // save при отсутствии правок отправлял savedFor('long') — в admin_config.json
  // уезжали мусорные ключи верхнего уровня (botDefaults.TakeProfit и т.п.), а
  // при сохранении без правок терялась вложенность {long, short}.
  // Ниже: состояние строго {long, short}, чтение — по режиму, сохранение —
  // полной структурой (патч сливается deepMerge в adminConfig.saveConfig).
  const savedAll = () => {
    const b = cfg.data?.botDefaults || {}
    return { long: { ...(b.long || {}) }, short: { ...(b.short || {}) } }
  }
  const current = (vals || savedAll())[tab] || {}
  const setFor = (m, key, val) =>
    setVals(v => {
      const base = v || savedAll()
      return { ...base, [m]: { ...(base[m] || {}), [key]: val } }
    })

  const FIELD_DEFS = [
    { key: 'TakeProfit',      label: 'Take Profit, %', hint: 'куда забираем прибыль' },
    { key: 'StopLoss',        label: 'Stop Loss, %',   hint: 'защитный стоп' },
    { key: 'Volume',          label: 'Объём, USDT',    hint: 'на сделку' },
    { key: 'Leverage',        label: 'Плечо, ×',       hint: '1–10' },
    { key: 'MinFundingRate',  label: 'Порог фандинга, %', hint: 'верхняя граница ставки (обычно −0.2)' },
    { key: 'MaxFundingRate',  label: 'Макс. ставка, %', hint: 'НИЖНЯЯ граница; пусто = без ограничения' },
  ]

  const save = async () => {
    if (saving) return
    setSaving(true); setNote('')
    try {
      // Сохраняем ОБЕ вкладки полной структурой — так в конфиге не появляется
      // мусорных ключей, а вторая вкладка не теряется, если её не открывали.
      await saveAdminConfig({ botDefaults: vals || savedAll() })
      setNote('✅ Дефолты сохранены — у новых ботов будут эти параметры')
      cfg.reload(); setVals(null)
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setSaving(false) }
  }

  return (
    <Card title="Параметры ботов по умолчанию" icon={SlidersHorizontal} accent={C.accent}>
      <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5, marginBottom: 10 }}>
        Эти параметры подставляются в форму создания бота у юзеров (LONG/SHORT)
        и в параметры виртуального «Пробного» бота. Юзер может изменить их в
        своей форме — здесь админ задаёт стартовые значения.
      </div>
      <ErrorLine onRetry={cfg.reload}>{cfg.error}</ErrorLine>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['long', 'short'].map(m => (
          <button key={m} type="button" onClick={() => { setTab(m); setVals(null); setNote('') }}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10,
              fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
              background: tab === m
                ? (m === 'long' ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                                : 'linear-gradient(135deg, #636366 0%, #48484A 100%)')
                : 'rgba(255,255,255,0.05)',
              color: tab === m ? '#fff' : '#636366',
            }}
          >
            {m === 'long' ? 'LONG · сбор фандинга' : 'SHORT · сбор падений'}
          </button>
        ))}
      </div>

      {FIELD_DEFS.map(f => (
        <div key={f.key} style={{ marginBottom: 10 }}>
          <div style={labelStyle}>{f.label}</div>
          <input
            type="number"
            step="0.01"
            value={current[f.key] ?? ''}
            onChange={e => setFor(tab, f.key, e.target.value)}
            placeholder={f.hint}
            style={inputStyle}
          />
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
        <Btn small icon={Save} disabled={saving} onClick={save}>
          {saving ? '…' : `Сохранить (${tab === 'long' ? 'LONG' : 'SHORT'})`}
        </Btn>
        <span style={{ fontSize: 10.5, color: C.dim, flex: 1 }}>
          Сохраняются обе вкладки сразу одним патчем конфига.
        </span>
      </div>
      {note && (
        <div style={{ fontSize: 11, marginTop: 6, color: note.startsWith('Ошибка') ? C.danger : C.accent }}>
          {note}
        </div>
      )}
    </Card>
  )
}

// «Режимы торговли» (task.txt п.7, 2026-09-17): админ может отключить LONG
// или SHORT целиком. Фронт скрывает кнопку направления, бэкенд отвечает 400
// на создание/смену отключённого режима; рекомендация в API автоматически
// переключается на оставшийся включённый.
function TradeModesCard() {
  const cfg = useAsync(() => getAdminConfig(), [])
  const [longOn, setLongOn] = useState(null)   // null = ещё не трогали
  const [shortOn, setShortOn] = useState(null) // null = ещё не трогали
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState('')

  const saved = cfg.data?.tradeModes || {}
  const longEnabled = longOn ?? saved.longEnabled !== false
  const shortEnabled = shortOn ?? saved.shortEnabled !== false
  const changed =
    (longOn !== null && longOn !== (saved.longEnabled !== false)) ||
    (shortOn !== null && shortOn !== (saved.shortEnabled !== false))

  const save = async () => {
    if (saving) return
    setSaving(true); setNote('')
    try {
      await saveAdminConfig({
        tradeModes: { longEnabled, shortEnabled },
      })
      setNote('✅ Сохранено')
      cfg.reload(); setLongOn(null); setShortOn(null)
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setSaving(false) }
  }

  return (
    <Card title="Режимы торговли" icon={Power} accent={C.warn}>
      <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5, marginBottom: 6 }}>
        Отключённое направление исчезает из формы создания бота у юзеров;
        бэкенд отклоняет создание и смену на отключённый режим. Текущие боты
        при этом не останавливаются.
      </div>
      <ErrorLine onRetry={cfg.reload}>{cfg.error}</ErrorLine>
      <Toggle
        on={longEnabled}
        onChange={v => { setLongOn(v); setNote('') }}
        label="Режим LONG"
        hint="Сбор фандинга. Выключите, если лонг-боты больше не нужны."
      />
      <Toggle
        on={shortEnabled}
        onChange={v => { setShortOn(v); setNote('') }}
        label="Режим SHORT"
        hint="Сбор падений после выплаты фандинга. Выключите, если шорт-боты больше не нужны."
      />
      {(!longEnabled || !shortEnabled) && (
        <div style={{
          marginTop: 8, fontSize: 11, color: C.warn, lineHeight: 1.45,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 10, padding: '8px 10px',
        }}>
          ⚠️ При выключенном режиме рекомендуется также перевести «Рекомендуемый
          режим» в карточке выше на оставшийся включённый — API сам переключит
          рекомендацию, но юзерам будет понятнее.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <Btn small icon={Save} disabled={!changed || saving} onClick={save}>
          {saving ? '…' : 'Сохранить'}
        </Btn>
        <span style={{ fontSize: 10.5, color: C.dim, flex: 1 }}>
          Сейчас: LONG — {longEnabled ? 'вкл' : 'ВЫКЛ'} · SHORT — {shortEnabled ? 'вкл' : 'ВЫКЛ'}
        </span>
      </div>
      {note && (
        <div style={{ fontSize: 11, marginTop: 6, color: note.startsWith('Ошибка') ? C.danger : C.accent }}>
          {note}
        </div>
      )}
    </Card>
  )
}

// Отдельная область «Результаты»: демо-боты юзеров. Демо-счёт — это тренировка
// юзера, и сводные цифры из «Торговли» тут не годятся: нужно видеть, У КОГО
// демо-бот торгует и с каким результатом, отдельно от реальных денег.
function DemoTradingCard() {
  const [view, setView] = useState('users')     // 'users' | 'bots'
  const demo = useAsync(() => getAdminDemoTrading({ limit: 50 }), [])
  const d = demo.data

  return (
    <Card
      title="Результаты"
      icon={FlaskConical}
      accent={C.warn}
      right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={demo.reload}>Обновить</Btn>}
    >
      <ErrorLine onRetry={demo.reload}>{demo.error}</ErrorLine>
      {demo.loading && !d ? <Loading /> : !d ? <Empty /> : (
        <>
          <div style={{ fontSize: 11, color: C.dim, marginBottom: 8, lineHeight: 1.45 }}>
            Результаты демо-ботов юзеров (только демо-счёт, is_demo = 1). Реальные сделки в эти цифры не входят.
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <BigStat label="Сделок сегодня" value={d.today?.trades ?? 0} color="#fff" />
            <BigStat
              label="PNL сегодня"
              value={fmtPnlFull(d.today?.pnl)}
              color={(d.today?.pnl || 0) >= 0 ? C.accent : C.danger}
            />
            <BigStat label="Юзеров" value={d.today?.traders ?? 0} color={C.indigo} />
          </div>

          <Row
            label="PNL за 7 дней"
            value={fmtPnlFull(d.week?.pnl)}
            valueColor={(d.week?.pnl || 0) >= 0 ? C.accent : C.danger}
            valueSub={`${d.week?.trades ?? 0} сделок · winrate ${d.week?.winRate ?? 0}%`}
            mono
          />
          <Row
            label="PNL за 30 дней"
            value={fmtPnlFull(d.month?.pnl)}
            valueColor={(d.month?.pnl || 0) >= 0 ? C.accent : C.danger}
            valueSub={`${d.month?.trades ?? 0} сделок`}
            mono
          />
          <Row
            label="PNL за всё время"
            value={fmtPnlFull(d.all?.pnl)}
            valueColor={(d.all?.pnl || 0) >= 0 ? C.accent : C.danger}
            valueSub={`${d.all?.trades ?? 0} сделок · ${d.all?.traders ?? 0} юзеров · winrate ${d.all?.winRate ?? 0}%`}
            mono
          />
          <Row label="Фандинг собран (демо, всё время)" value={fmtPnlFull(d.all?.funding)} mono />
          <Row
            label="Демо-ботов в базе"
            value={`${(d.botsByStatus?.active || 0)} работают / ${Object.values(d.botsByStatus || {}).reduce((s, x) => s + x, 0)} всего`}
          />

          <div style={divider} />
          <Chips
            options={[
              { key: 'users', label: 'По юзерам', count: (d.users || []).length },
              { key: 'bots',  label: 'По ботам',  count: (d.bots || []).length },
            ]}
            value={view}
            onChange={setView}
          />

          {/* task.txt, 2026-09-30: список в разделе «Результаты» тоже листается
              в своей рамке — он растёт вместе с числом демо-ботов. */}
          {view === 'users' ? (
            !(d.users || []).length ? <Empty>Демо-сделок нет</Empty> : (
              <div style={{ maxHeight: 320, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                {d.users.map(u => (
                  <Row
                    key={u.user_id}
                    label={`${userLabel(u)} · id ${u.user_id}`}
                    value={fmtPnlFull(u.pnl)}
                    valueColor={u.pnl >= 0 ? C.accent : C.danger}
                    valueSub={`${u.trades} сделок · winrate ${u.winRate}% · посл. ${fmtDateTime(u.lastClosedAt)}`}
                    mono
                  />
                ))}
              </div>
            )
          ) : (
            !(d.bots || []).length ? <Empty>Демо-сделок нет</Empty> : (
              <div style={{ maxHeight: 320, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                {d.bots.map(b => (
                  <Row
                    key={`${b.user_id}:${b.bot_id}`}
                    label={`${b.botName || b.bot_id || '—'} · ${userLabel(b)}`}
                    value={fmtPnlFull(b.pnl)}
                    valueColor={b.pnl >= 0 ? C.accent : C.danger}
                    valueSub={`${b.trades} сделок · winrate ${b.winRate}% · ${b.status || 'бот удалён'} · посл. ${fmtDateTime(b.lastClosedAt)}`}
                    mono
                  />
                ))}
              </div>
            )
          )}

          {!!(d.topPairs || []).length && (
            <>
              <div style={divider} />
              <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Топ демо-пар за 30 дней</div>
              {d.topPairs.map(p => (
                <Row
                  key={p.pair}
                  label={p.pair}
                  value={fmtPnlFull(p.pnl)}
                  valueColor={p.pnl >= 0 ? C.accent : C.danger}
                  valueSub={`${p.trades} сделок`}
                  mono
                />
              ))}
            </>
          )}
        </>
      )}
    </Card>
  )
}

export default function BotsSection() {
  const [status, setStatus] = useState('all')
  // Сколько ботов загружено в список (task.txt, 2026-09-30): порция, а не всё
  // сразу — раздел не растёт вместе со списком. «Показать ещё» добавляет порцию.
  const [limit, setLimit] = useState(LB_PAGE)
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')

  const bots = useAsync(() => getAdminBots({ status, limit }), [status, limit])
  const trading = useAsync(() => getAdminTrading(), [])

  // ── Снимок ботов (task.txt п.5): «Запомнить все данные» перед остановкой/
  // рестартом → «Запустить все из снимка» после. Снимок хранится в БД и
  // переживает рестарт; кнопки показывают, что запомнено и когда.
  const snap = useAsync(() => getAdminBotSnapshot(), [])
  const [snapBusy, setSnapBusy] = useState('')   // '' | 'save' | 'restore'
  const [snapNote, setSnapNote] = useState('')
  const [snapDone, setSnapDone] = useState(null) // результат последнего restore

  const saveSnap = async () => {
    if (snapBusy) return
    if (!(await confirmAsync('Запомнить все запущенные боты (кто у какого юзера работает)? Это действие перед рестартом/остановкой ботов.'))) return
    setSnapBusy('save'); setSnapNote(''); setSnapDone(null)
    try {
      const r = await adminSaveBotSnapshot()
      setSnapNote(`✅ Запомнено запущенных ботов: ${r.count || 0}`)
      snap.reload()
    } catch (e) { setSnapNote(`Ошибка: ${e?.message || e}`) }
    finally { setSnapBusy('') }
  }

  const restoreSnap = async () => {
    if (snapBusy) return
    if (!(await confirmAsync('Запустить все боты из снимка? Сделайте это ПОСЛЕ перезагрузки сервера, когда боты остановлены.'))) return
    setSnapBusy('restore'); setSnapNote(''); setSnapDone(null)
    try {
      const r = await adminRestoreBots()
      setSnapDone(r)
      const reasons = (r.skipped || []).map(x => x.reason)
      const nSub = reasons.filter(x => x === 'no_subscription').length
      const nTrial = reasons.filter(x => x === 'trial_finished').length
      const nActive = reasons.filter(x => x === 'already_active').length
      const extra = [
        nActive ? `уже работали: ${nActive}` : '',
        nSub ? `нет подписки: ${nSub}` : '',
        nTrial ? `пробный исчерпан: ${nTrial}` : '',
      ].filter(Boolean).join(' · ')
      // failed — боты, которых поднять НЕ удалось (например, упёрлись в
      // MAX_CONCURRENT_BOTS или RUN.py не найден). Раньше такие молча
      // растворялись в «Запущено: 0» — теперь видно, сколько и почему.
      const nFailed = (r.failed || []).length
      setSnapNote(
        `✅ Запущено: ${(r.started || []).length}`
        + (extra ? ` (пропущено — ${extra})` : '')
        + ((r.missing || []).length ? ` · не найдено: ${r.missing.length}` : '')
        + (nFailed ? ` · НЕ удалось запустить: ${nFailed}` : ''),
      )
      bots.reload()
      snap.reload()
    } catch (e) { setSnapNote(`Ошибка: ${e?.message || e}`) }
    finally { setSnapBusy('') }
  }

  const by = bots.data?.byStatus || {}
  const byMode = bots.data?.byMode || {}
  const rows = bots.data?.rows || []
  const zombies = rows.filter(b => b.status === 'active' && !b.alive).length
  // Боты, которым биржа отклоняет ордера: бот «работает», но ни одной сделки не
  // откроет. Самая частая причина — деньги остались на счёте пополнения внутри
  // биржи, а торговый счёт пуст (см. TG-BOT/src/botHealth.js). Считается по
  // ЗАГРУЖЕННОЙ порции: health приходит только для строк текущей страницы.
  const notTrading = rows.filter(b => b.health).length
  // Сколько ботов ВСЕГО под текущим фильтром (byStatus считает всю БД, не
  // загруженную порцию) — из этого и «Показано N из M», и «Показать ещё».
  const total = status === 'all'
    ? (by.active || 0) + (by.stopped || 0) + (by.error || 0)
    : (by[status] || 0)
  const canLoadMore = total > rows.length && limit < LB_PAGE_MAX

  const chips = [
    { key: 'all',     label: 'Все' },
    { key: 'active',  label: 'Работают', count: by.active || 0 },
    { key: 'stopped', label: 'Остановлены', count: by.stopped || 0 },
    { key: 'error',   label: 'Ошибка', count: by.error || 0 },
  ]

  const stop = async (b) => {
    if (!(await confirmAsync(`Остановить бот «${b.name || b.bot_id}» юзера ${b.user_id}?`))) return
    const key = `${b.user_id}:${b.bot_id}`
    setBusy(key); setNote('')
    try {
      await adminStopBot(b.user_id, b.bot_id)
      setNote(`Бот ${b.bot_id} остановлен`)
      bots.reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const t = trading.data

  return (
    <div>
      <Card title="Боты" icon={Bot} right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={bots.reload}>Обновить</Btn>}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <BigStat label="Всего" value={(by.active || 0) + (by.stopped || 0) + (by.error || 0)} color="#fff" />
          <BigStat label="Работают" value={by.active || 0} color={C.accent} />
          <BigStat label="Зомби" value={zombies} color={zombies ? C.warn : C.dim} />
          <BigStat label="Не торгуют" value={notTrading} color={notTrading ? C.danger : C.dim} />
        </div>
        <Row label="Реальный счёт / демо" value={`${byMode.real || 0} / ${byMode.demo || 0}`} />
        {notTrading > 0 && (
          <div style={{
            marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.28)',
            borderRadius: 12, padding: '9px 11px',
          }}>
            <AlertTriangle size={14} color={C.danger} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 11.5, color: C.danger, lineHeight: 1.45 }}>
              {notTrading} бот(ов) в загруженном списке получают <b>отказ биржи</b> — сделки
              не открываются. Причина и ответ биржи — в карточке бота.
              {rows.some(b => b.health?.hint === 'no_funds') && (
                <>
                  {' '}Похоже на пустой <b>торговый счёт</b>: деньги юзера остались на счёте
                  пополнения внутри биржи. На демо-счёте так не бывает — там счёт не
                  разделён, поэтому демо-бот торгует, а реальный нет.
                </>
              )}
            </div>
          </div>
        )}
        {zombies > 0 && (
          <div style={{
            marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 12, padding: '9px 11px',
          }}>
            <AlertTriangle size={14} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 11.5, color: C.warn, lineHeight: 1.45 }}>
              {zombies} бот(ов) помечены работающими, но процесса нет. Юзер видит «работает»,
              а сделок не будет — остановите их кнопкой ниже.
            </div>
          </div>
        )}

        <div style={divider} />
        <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5, marginBottom: 8 }}>
          Перед выкладкой изменений / перезапуском сервера нажмите{' '}
          <b style={{ color: '#fff' }}>«Запомнить все данные»</b> — сохранится список
          запущенных ботов и у кого из юзеров. После перезапуска вернитесь сюда и
          нажмите <b style={{ color: '#fff' }}>«Запустить все из снимка»</b> — боты этих
          юзеров запустятся снова: с планировщиком их подхватит ближайший проход :58,
          без планировщика они стартуют сразу, как по кнопке «Старт».
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn small icon={Save} color={C.accent} disabled={!!snapBusy} onClick={saveSnap}>
            {snapBusy === 'save' ? '…' : 'Запомнить все данные'}
          </Btn>
          <Btn small icon={Play} color={C.indigo} disabled={!!snapBusy} onClick={restoreSnap}>
            {snapBusy === 'restore' ? '…' : 'Запустить все из снимка'}
          </Btn>
          {snap.data?.saved_at && (
            <span style={{ fontSize: 10.5, color: C.dim }}>
              Снимок: {fmtDateTime(snap.data.saved_at)} · {snap.data.count} бот(ов)
            </span>
          )}
        </div>
        {snapNote && (
          <div style={{
            fontSize: 11.5, marginTop: 8, lineHeight: 1.45,
            color: (snapNote.startsWith('Ошибка') || (snapDone?.failed || []).length) ? C.danger : C.accent,
          }}>
            {snapNote}
          </div>
        )}
        {!!(snapDone?.started || []).length && (
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 6, lineHeight: 1.6, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {snapDone.started.map(x => `♻️ ${x.name || x.bot_id} · юзер ${x.uid}`).join('\n')}
          </div>
        )}
        {!!(snapDone?.failed || []).length && (
          <div style={{ fontSize: 10.5, color: C.danger, marginTop: 6, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {snapDone.failed.map(x => `⚠️ юзер ${x.uid} · бот ${x.bot_id} — ${x.detail}`).join('\n')}
          </div>
        )}
      </Card>

      {/* Рекомендуемый режим для юзеров (ручной выбор админом) + аналитика рынка */}
      <RecommendModeCard />

      {/* Параметры ботов по умолчанию (task.txt п.6) и режимы торговли
          (task.txt п.7): дефолты TP/SL/Volume/Leverage/порогов фандинга для
          формы создания бота; тумблеры LONG/SHORT доступности направлений. */}
      <BotDefaultsCard />
      <TradeModesCard />

      <Chips
        options={chips}
        value={status}
        onChange={(k) => { setStatus(k); setLimit(LB_PAGE) }}   // смена фильтра — снова первая порция
      />

      <ErrorLine onRetry={bots.reload}>{bots.error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {bots.loading && !rows.length ? <Loading /> : !rows.length ? <Empty>Ботов нет</Empty> : (
        <Card style={{ padding: '6px 14px' }}>
          {/* task.txt, 2026-09-30: раздел с информацией о ботах по юзерам не
              должен растягиваться на всю страницу — список прокручивается
              внутри своей рамки. Видно, сколько ботов всего и сколько открыто. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, padding: '8px 0 6px', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 10.5, color: C.dim }}>
              Показано {fmtInt(rows.length)} из {fmtInt(total)}
              {total > rows.length && !canLoadMore ? ` (первые ${fmtInt(LB_PAGE_MAX)})` : ''}
              <span style={{ color: C.muted }}> · список листается</span>
            </span>
            {canLoadMore && (
              <Btn
                small ghost color={C.muted}
                disabled={bots.loading}
                onClick={() => setLimit(l => Math.min(LB_PAGE_MAX, l + LB_PAGE))}
              >
                {bots.loading ? '…' : `Показать ещё ${fmtInt(Math.min(LB_PAGE, total - rows.length))}`}
              </Btn>
            )}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', overscrollBehavior: 'contain' }}>
            {rows.map((b, i) => {
            const st = STATUS[b.status] || { label: b.status, color: C.muted }
            const zombie = b.status === 'active' && !b.alive
            const key = `${b.user_id}:${b.bot_id}`
            // Направление и настройки бота для карточки (task.txt п.3): всё живёт
            // в bots.params / algo_params / virtual, которые adminBotsOverview
            // теперь отдаёт на фронт. Направление — TradeDirection (LONG/SHORT).
            const p = b.params || {}
            const dir = String(p.TradeDirection || 'LONG').toUpperCase()
            // stats приходит с бэкенда (adminBotsOverview): сделки/PNL/winrate
            // именно этого бота. Пустой объект — на случай старого ответа API.
            const bs = b.stats || {}
            const cfgBits = [dir]
            if (p.Leverage != null) cfgBits.push(`плечо ${p.Leverage}x`)
            if (p.Volume != null) cfgBits.push(`объём $${p.Volume}`)
            if (p.MinFundingRate != null) cfgBits.push(`порог ${p.MinFundingRate}%`)
            if (b.virtual) cfgBits.push('ПРОБНЫЙ')
            const dirColor = dir === 'SHORT' ? '#8e8e93' : C.accent
            return (
              <div key={key} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.name || b.bot_id}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                      {userLabel(b)} (id {b.user_id}) · {b.exchange} · {b.account_mode === 'demo' ? 'демо' : 'реальный'}
                    </div>
                    {cfgBits.length > 1 && (
                      <div style={{ fontSize: 10, marginTop: 2 }}>
                        <span style={{ fontWeight: 700, color: dirColor, fontFamily: 'monospace' }}>{dir}</span>
                        <span style={{ color: C.dim, fontFamily: 'monospace' }}> · {cfgBits.slice(1).join(' · ')}</span>
                      </div>
                    )}
                    {/* PNL и сделки ЭТОГО бота в его же карточке (задача владельца
                        2026-10-07: «не вижу в админке PNL бота этого юзера»). Раньше
                        в разделе были только общий котёл «Торговля» по всему проекту и
                        демо-разрез «Результаты» — по конкретному боту цифр не было.
                        Считает бэкенд по (user_id, bot_id) и только по счёту этого
                        бота: у реального — реальные сделки, у демо — демо. Ноль сделок
                        пишем словами: так сразу видно бота, который не торгует (например,
                        биржа отклоняет ордера — причина в блоке ниже). */}
                    <div style={{ fontSize: 10.5, color: C.dim, marginTop: 3, fontFamily: 'monospace' }}>
                      {bs.trades ? (
                        <>
                          сделок {fmtInt(bs.trades)} · PNL{' '}
                          <span style={{ color: bs.pnl >= 0 ? C.accent : C.danger, fontWeight: 700 }}>
                            {fmtPnlFull(bs.pnl)}
                          </span>{' '}
                          USDT · winrate {bs.winRate}%
                          {bs.lastClosedAt ? ` · последняя ${fmtClosedAt(bs.lastClosedAt)}` : ''}
                        </>
                      ) : 'сделок пока нет'}
                    </div>
                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2, fontFamily: 'monospace' }}>
                      pid {b.pid || '—'} · обновлён {fmtDateTime(b.updated_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                    <Pill color={zombie ? C.warn : st.color}>{zombie ? 'зомби' : st.label}</Pill>
                    {b.health && <Pill color={C.danger}>не торгует</Pill>}
                    {b.status === 'active' && (
                      <Btn small ghost color={C.danger} icon={Square} disabled={busy === key} onClick={() => stop(b)}>
                        {busy === key ? '…' : 'Стоп'}
                      </Btn>
                    )}
                  </div>
                </div>
                {b.health && (
                  <div style={{
                    marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-start',
                    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.28)',
                    borderRadius: 10, padding: '7px 9px',
                  }}>
                    <AlertTriangle size={13} color={C.danger} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 11, color: C.danger, lineHeight: 1.45, overflowWrap: 'anywhere', minWidth: 0 }}>
                      {/* Два разных случая. «Биржа отклонила ордер» — про конкретную
                          сделку (объём, маржа, монета). «Ключ биржи не работает» —
                          про ключ: тут ни монеты, ни кода биржи в причине нет, и
                          бот не откроет ни одной сделки, пока ключ не заменят. */}
                      <b>{b.health.kind === 'api_key_dead' ? 'Ключ биржи не работает' : 'Биржа отклонила ордер'}</b>
                      {b.health.symbol ? ` · ${b.health.symbol}` : ''}
                      {b.health.retCode != null ? ` · код ${b.health.retCode}` : ''}
                      {': '}
                      <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: 10.5 }}>
                        {b.health.exchangeMsg}
                      </span>
                      {b.health.hintText && (
                        <div style={{ marginTop: 3 }}>{b.health.hintText}</div>
                      )}
                      <div style={{ marginTop: 3, fontSize: 10, color: C.dim }}>
                        источник: {b.health.source}
                        {b.health.at ? ` · ${fmtDateTime(b.health.at)}` : ''}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          </div>
        </Card>
      )}

      <Card
        title="Торговля"
        icon={TrendingUp}
        accent={C.indigo}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={trading.reload}>Обновить</Btn>}
      >
        <ErrorLine onRetry={trading.reload}>{trading.error}</ErrorLine>
        {trading.loading && !t ? <Loading /> : !t ? <Empty /> : (
          <>
            <TradeBucket title="Реальные · сегодня" b={t.real?.today} />
            <TradeBucket title="Реальные · 7 дней" b={t.real?.week} />
            <TradeBucket title="Реальные · 30 дней" b={t.real?.month} />
            <div style={divider} />
            <Row label="Всего реальных сделок" value={t.real?.all?.trades ?? 0} />
            <Row
              label="PNL за всё время"
              value={`${fmtMoney(t.real?.all?.pnl)} USDT`}
              valueColor={(t.real?.all?.pnl || 0) >= 0 ? C.accent : C.danger}
            />
            <Row label="Фандинг собран (всё время)" value={fmtMoney(t.real?.all?.funding)} />
            <Row label="Демо · сделок сегодня" value={t.demo?.today?.trades ?? 0} />
            <Row label="Трейдеров за 7 дней" value={t.traders ?? 0} valueColor={C.indigo} />
            {!!(t.topPairs || []).length && (
              <>
                <div style={divider} />
                <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Топ пар за 30 дней</div>
                {t.topPairs.map(p => (
                  <Row
                    key={p.pair}
                    label={p.pair}
                    value={`${fmtMoney(p.pnl)} USDT`}
                    valueColor={p.pnl >= 0 ? C.accent : C.danger}
                    valueSub={`${p.trades} сделок`}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Card>

      {/* Раздел «Результаты» — демо-боты юзеров (по задаче: PNL демо-ботов) */}
      <DemoTradingCard />
    </div>
  )
}
